import { forwardRef, useCallback, useEffect, useRef, type MutableRefObject } from "react"
import type { Project } from "@/types/portfolio"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ProjectGallery } from "./ProjectGallery"

interface ProjectSectionProps {
  project: Project
  sectionIndex: number
}

export const ProjectSection = forwardRef<HTMLElement, ProjectSectionProps>(
  function ProjectSection({ project, sectionIndex }, ref) {
    const localSectionRef = useRef<HTMLElement | null>(null)
    const carouselRef     = useRef<HTMLDivElement>(null)

    // Carousel fade-in on scroll
    useEffect(() => {
      const carousel = carouselRef.current
      const section  = localSectionRef.current
      if (!carousel || !section) return

      gsap.set(carousel, {
        opacity: 0,
        y: 24,
        rotateX: 30,
        rotateY: 30,
        rotateZ: -10,
        transformPerspective: 1400,
      })

      const trigger = ScrollTrigger.create({
        trigger: section,
        start: "top 82%",
        toggleActions: "play none none none",
        onEnter: () => {
          gsap.to(carousel, { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" })
        },
      })

      return () => { trigger.kill(); gsap.set(carousel, { clearProps: "all" }) }
    }, [])

    const setRef = useCallback(
      (el: HTMLElement | null) => {
        localSectionRef.current = el
        if (typeof ref === "function") ref(el)
        else if (ref) (ref as MutableRefObject<HTMLElement | null>).current = el
      },
      [ref],
    )

    return (
      <section
        ref={setRef}
        data-section={`project-${project.id}`}
        data-project-id={project.id}
        data-section-index={sectionIndex}
        className="overflow-x-clip py-32 md:py-48"
      >
        <div ref={carouselRef} data-project-carousel>
          <ProjectGallery
            images={project.images}
            projectType={project.projectType}
          />
        </div>
      </section>
    )
  },
)
